import i18n, { InitOptions, Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, Locale, SUPPORTED_LOCALES } from './locales';
import LanguageDetector from 'i18next-browser-languagedetector';

// Dynamically import all locale JSON files using Vite's glob import
// This automatically picks up any new translation files without manual imports
const localeModules = import.meta.glob('../locales/**/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;

// Build resources object dynamically from imported modules
const buildResources = (): Resource => {
  const resources: Resource = {};

  for (const path in localeModules) {
    // Extract locale and namespace from path
    // Path format: ../locales/{locale}/{namespace}.json
    const match = /\.\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (!match) {
      continue;
    }

    const [, locale, namespace] = match;

    if (!resources[locale]) {
      resources[locale] = {};
    }

    // Get the module content (handle both default export and direct object)
    const moduleContent = localeModules[path];
    (resources[locale] as Record<string, unknown>)[namespace] = moduleContent.default ?? moduleContent;
  }

  return resources;
};

const baseLanguageCodes = [...new Set(SUPPORTED_LOCALES.map((locale) => locale.split('-')[0]))];

const buildFallbackLng = (): Record<string, string[]> => {
  const fallbacks: Record<string, string[]> = { default: [DEFAULT_LOCALE] };
  for (const locale of SUPPORTED_LOCALES) {
    const baseCode = locale.split('-')[0];
    if (!fallbacks[baseCode]) {
      fallbacks[baseCode] = [locale];
    }
  }
  fallbacks['en-US'] = ['en-GB'];
  return fallbacks;
};

i18n.use(LanguageDetector);

const i18nParams: Partial<InitOptions> = {
  supportedLngs: [...SUPPORTED_LOCALES, ...baseLanguageCodes, 'en-US'],
  detection: {
    // Only detect from browser, don't cache to localStorage
    // On sign in, useAuth calls i18n.changeLanguage() with user's profile language
    // On sign out, i18n.changeLanguage() without args re-detects from browser
    order: ['navigator'],
    caches: [],
  },
};

const syncDocumentHtmlLang = () => {
  if (typeof document === 'undefined') {
    return;
  }
  const lang = i18n.resolvedLanguage ?? DEFAULT_LOCALE;
  document.documentElement.lang = lang;
};

i18n.on('languageChanged', syncDocumentHtmlLang);
i18n.on('initialized', syncDocumentHtmlLang);

i18n.use(initReactI18next).init({
  ...i18nParams,
  fallbackLng: buildFallbackLng(),
  nonExplicitSupportedLngs: false,
  load: 'currentOnly',
  defaultNS: 'index',
  resources: buildResources(),
  interpolation: {
    escapeValue: false,
  },
  debug: import.meta.env.NODE_ENV === 'development',
});

const changeLanguage = (locale?: Locale) => {
  i18n.changeLanguage(locale);
};

export { changeLanguage };
export { default } from 'i18next';
